import { useEffect, useRef, useState } from 'react';
import { Search, Users as UsersIcon, X } from 'lucide-react';
import DarkSelect from '../../DarkSelect';
import DatePicker from '../../DatePicker';
import {
  ACTIVITY_TYPE_META, ACTIVITY_TYPE_ORDER, RESULT_META, RESULT_ORDER, FACILITY_TZ,
  type ActivityType, type LoginResult,
} from './auditConstants';

export type DatePreset = 'today' | '7d' | '30d' | '90d' | 'custom';

export interface AuditRange {
  preset: DatePreset;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

const PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'custom', label: 'Custom' },
];

// Bug fix (2026-10-17): this used to be `d.toISOString().slice(0, 10)` — the UTC calendar date,
// not the Eastern one. For anyone west of UTC in the evening, "today" in UTC is already
// "tomorrow" relative to Eastern time, so the date sent to the server for both `from` and `to`
// was off by a day around the UTC midnight boundary. The server interprets from/to as Eastern
// calendar dates (lib/audit.ts facilityDayStart/facilityDayEnd) — this must match, or "Today"/
// "7 days" silently exclude rows that are, in Eastern terms, genuinely within range.
function toIsoDate(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: FACILITY_TZ }).format(d);
}

export function rangeForPreset(preset: DatePreset, custom?: { from: string; to: string }): AuditRange {
  const now = new Date();
  if (preset === 'custom' && custom) return { preset, from: custom.from, to: custom.to };
  const to = toIsoDate(now);
  let from = to;
  if (preset === '7d') from = toIsoDate(new Date(now.getTime() - 7 * 86400000));
  else if (preset === '30d') from = toIsoDate(new Date(now.getTime() - 30 * 86400000));
  else if (preset === '90d') from = toIsoDate(new Date(now.getTime() - 90 * 86400000));
  return { preset, from, to };
}

const fieldStyle: React.CSSProperties = {
  height: 36, padding: '0 12px', borderRadius: 6,
  background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
  color: 'var(--text-primary)', fontSize: 13,
  display: 'flex', alignItems: 'center', gap: 6,
};

interface Props {
  view: 'logins' | 'activity';
  range: AuditRange;
  onRangeChange: (r: AuditRange) => void;
  userId: string;
  onUserIdChange: (v: string) => void;
  userOptions: { value: string; label: string }[];
  search: string;
  onSearchChange: (v: string) => void;
  resultFilter: LoginResult[];
  onResultFilterChange: (v: LoginResult[]) => void;
  typeFilter: ActivityType[];
  onTypeFilterChange: (v: ActivityType[]) => void;
  resultCounts: Partial<Record<LoginResult, number>>;
  typeCounts: Partial<Record<ActivityType, number>>;
}

export default function AuditFilterBar({
  view, range, onRangeChange, userId, onUserIdChange, userOptions,
  search, onSearchChange, resultFilter, onResultFilterChange, typeFilter, onTypeFilterChange,
  resultCounts, typeCounts,
}: Props) {
  const [searchLocal, setSearchLocal] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(searchLocal), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLocal]);

  const isDefault = range.preset === '7d' && !userId && !search && resultFilter.length === 0 && typeFilter.length === 0;

  function clearAll() {
    onRangeChange(rangeForPreset('7d'));
    onUserIdChange('');
    setSearchLocal('');
    onSearchChange('');
    onResultFilterChange([]);
    onTypeFilterChange([]);
  }

  function toggleResult(r: LoginResult) {
    onResultFilterChange(resultFilter.includes(r) ? resultFilter.filter((x) => x !== r) : [...resultFilter, r]);
  }
  function toggleType(t: ActivityType) {
    onTypeFilterChange(typeFilter.includes(t) ? typeFilter.filter((x) => x !== t) : [...typeFilter, t]);
  }

  return (
    <div
      className="audit-in"
      style={{
        padding: '14px 16px', borderRadius: 14, background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      {/* Row 1 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div className="audit-preset-track" style={{ display: 'flex', padding: 3, borderRadius: 8 }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className={`audit-preset-btn${range.preset === p.id ? ' active' : ''}`}
              onClick={() => onRangeChange(p.id === 'custom' ? { ...range, preset: 'custom' } : rangeForPreset(p.id))}
              style={{ height: 30, padding: '0 12px', borderRadius: 6, fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {range.preset === 'custom' && (
          <div className="audit-in" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DatePicker value={range.from} onChange={(v) => onRangeChange({ ...range, from: v })} max={range.to} fitContent />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <DatePicker value={range.to} onChange={(v) => onRangeChange({ ...range, to: v })} min={range.from} fitContent />
          </div>
        )}

        <div style={{ minWidth: 170 }}>
          <DarkSelect
            value={userId}
            onChange={onUserIdChange}
            options={userOptions}
            placeholder="All users"
            minWidth={170}
            icon={<UsersIcon size={14} color="var(--text-muted)" />}
          />
        </div>

        <div style={{ ...fieldStyle, flex: '1 1 220px' }}>
          <Search size={14} color="var(--text-muted)" />
          <input
            value={searchLocal}
            onChange={(e) => setSearchLocal(e.target.value)}
            placeholder={view === 'logins' ? 'Search user, email, IP or location' : 'Search user, record, PO #, item…'}
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, minWidth: 0 }}
            onFocus={(e) => { e.currentTarget.parentElement!.style.borderColor = '#3b82f6'; e.currentTarget.parentElement!.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
            onBlur={(e) => { e.currentTarget.parentElement!.style.borderColor = 'var(--border-default)'; e.currentTarget.parentElement!.style.boxShadow = 'none'; }}
          />
        </div>

        {!isDefault && (
          <button
            className="btn btn-ghost"
            style={{ height: 36 }}
            onClick={clearAll}
          >
            <X size={14} /> Clear filters
          </button>
        )}
      </div>

      {/* Row 2 — chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 4 }}>
          {view === 'logins' ? 'Result' : 'Activity type'}
        </span>
        {view === 'logins'
          ? RESULT_ORDER.filter((r) => r !== 'locked' || (resultCounts.locked ?? 0) > 0).map((r) => {
              const meta = RESULT_META[r];
              const active = resultFilter.includes(r);
              const count = resultCounts[r] ?? 0;
              return (
                <button
                  key={r}
                  className={`audit-chip${r === 'blocked' && count > 0 ? ' audit-pulse' : ''}`}
                  onClick={() => toggleResult(r)}
                  style={{
                    height: 28, padding: '0 11px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    background: active ? `${meta.color}26` : 'transparent',
                    border: `1px solid ${active ? `${meta.color}80` : 'var(--border-default)'}`,
                    color: active ? meta.color : 'var(--text-secondary)',
                  }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                  {meta.label} <span style={{ fontWeight: 500, opacity: 0.7 }}>{count}</span>
                </button>
              );
            })
          : ACTIVITY_TYPE_ORDER.map((t) => {
              const meta = ACTIVITY_TYPE_META[t];
              const active = typeFilter.includes(t);
              const count = typeCounts[t] ?? 0;
              return (
                <button
                  key={t}
                  className="audit-chip"
                  onClick={() => toggleType(t)}
                  style={{
                    height: 28, padding: '0 11px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    background: active ? `${meta.color}26` : 'transparent',
                    border: `1px solid ${active ? `${meta.color}80` : 'var(--border-default)'}`,
                    color: active ? meta.color : 'var(--text-secondary)',
                  }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                  {meta.label} <span style={{ fontWeight: 500, opacity: 0.7 }}>{count}</span>
                </button>
              );
            })}
      </div>
    </div>
  );
}
